import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import type { WebhookResponse } from '../types/forms';
import type { RequestType } from '../lib/validation';
import { isOidcConfigured } from '../lib/auth';
import { uploadToBlob } from '../lib/blob-upload';

interface UseFormSubmitReturn<T> {
  submit: (data: T, requestType: RequestType, files?: File[]) => Promise<WebhookResponse>;
  isSubmitting: boolean;
  error: string | null;
}

export function useFormSubmit<T>(): UseFormSubmitReturn<T> {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = isOidcConfigured() ? useAuth() : null; // eslint-disable-line react-hooks/rules-of-hooks

  const submit = async (
    data: T,
    requestType: RequestType,
    files?: File[]
  ): Promise<WebhookResponse> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        formType: requestType,
        submittedAt: new Date().toISOString(),
        data,
      };

      const accessToken = auth?.user?.access_token;

      // Files go to Vercel Blob first; /api/marketing re-inlines them for n8n
      // (the JSON post itself must stay under Vercel's ~4.5 MB body limit).
      let attachments: { name: string; mimeType: string; url: string }[] = [];
      if (files && files.length > 0) {
        attachments = await Promise.all(
          files.map(async (file) => {
            const blob = await uploadToBlob(file, 'marketing', accessToken);
            return { name: blob.name, mimeType: blob.type, url: blob.url };
          })
        );
      }

      const payloadWithAttachments = {
        ...payload,
        ...(attachments.length > 0 ? { attachments } : {}),
      };

      const response = await fetch('/api/marketing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(payloadWithAttachments),
      });

      if (!response.ok) {
        throw new Error(`Something went wrong (error ${response.status}). Please try again.`);
      }

      const result: WebhookResponse = await response.json();
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again or contact support.';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting, error };
}
